import React, { createContext, useContext, useState, useEffect } from 'react';
import { Rocket, Trophy, Heart, MessageSquare, ClipboardList, BarChart3, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Theme configurations
const themeConfigs = {
  space: {
    id: 'space',
    name: 'Space Mission',
    tagline: 'Mission Control Platform',
    icon: Rocket,
    useLightMode: false,
    colors: {
      primary: '#00f3ff',
      secondary: '#bc13fe',
      accent: '#ff00aa',
      gradient: 'from-cyan-500 to-purple-600',
      glow: 'rgba(0, 243, 255, 0.4)',
      background: '#050508',
      cardBg: 'rgba(10, 10, 15, 0.8)',
      textPrimary: '#ffffff',
      textSecondary: 'rgba(255, 255, 255, 0.8)',
      textMuted: 'rgba(255, 255, 255, 0.4)',
    },
    terminology: {
      appName: "Callen's Mission",
      userRole: 'Pilot',
      dayLabel: 'Mission Day',
      dashboard: 'Mission Control',
      communication: 'Communication',
      communicationDesc: 'Send a signal',
      communicationAction: 'Send Signal',
      missionUpdate: 'Mission Update',
      missionUpdateDesc: 'Log your status',
      missionStats: 'Mission Stats',
      missionStatsDesc: 'View progress',
      mayday: 'Mayday',
      maydayDesc: 'Emergency Info',
      riskMeter: 'Mission Risk Meter',
      riskMeterDesc: 'How risky does it feel right now?',
      freezeLog: 'Freeze Log',
      freezeLogDesc: 'What triggered the freeze?',
      dailyCheckin: 'Daily Check-in',
      dailyCheckinDesc: 'How did today feel overall?',
      streak: 'Mission Streak',
      courageLogs: 'Courage Logs',
      signalsSent: 'Signals Sent',
      encouragement: 'Every mission starts with one step. You\'ve got this, Pilot.',
      statsEncouragement: 'Every log is a step forward. Keep going, Pilot.',
    },
    quickMessages: [
      "I'm okay",
      "Need space",
      "Feeling stressed",
      "Want to talk",
      "Having a good day",
    ],
  },
  sports: {
    id: 'sports',
    name: 'Game Day',
    tagline: 'Game Day Headquarters',
    icon: Trophy,
    useLightMode: true,
    colors: {
      primary: '#EA580C',
      secondary: '#1E40AF',
      accent: '#F59E0B',
      gradient: 'from-orange-600 to-blue-700',
      glow: 'rgba(234, 88, 12, 0.4)',
      background: '#FAFAFA',
      cardBg: '#FFFFFF',
      textPrimary: '#1F2937',
      textSecondary: '#4B5563',
      textMuted: '#9CA3AF',
    },
    terminology: {
      appName: 'Game Day HQ',
      userRole: 'Athlete',
      dayLabel: 'Game Day',
      dashboard: 'Locker Room',
      communication: 'Huddle Up',
      communicationDesc: 'Call a timeout',
      communicationAction: 'Send Message',
      missionUpdate: 'Game Check-in',
      missionUpdateDesc: 'Log your performance',
      missionStats: 'Performance Stats',
      missionStatsDesc: 'View your scores',
      mayday: 'Timeout',
      maydayDesc: 'Emergency Info',
      riskMeter: 'Pressure Meter',
      riskMeterDesc: 'How much pressure are you feeling?',
      freezeLog: 'Benched Moments',
      freezeLogDesc: 'What took you out of the game?',
      dailyCheckin: 'Post-Game Review',
      dailyCheckinDesc: 'How did today\'s game go?',
      streak: 'Win Streak',
      courageLogs: 'Plays Made',
      signalsSent: 'Huddles Called',
      encouragement: 'Champions are made one play at a time. You\'ve got this, Athlete.',
      statsEncouragement: 'Every practice makes you stronger. Keep pushing, Champ.',
    },
    quickMessages: [
      "I'm good",
      "Need a break",
      "Feeling the pressure",
      "Ready to talk",
      "Winning today",
    ],
  },
  pet: {
    id: 'pet',
    name: 'Pet Parent',
    tagline: 'Pet Parent Headquarters',
    icon: Heart,
    useLightMode: true,
    colors: {
      primary: '#EC4899',
      secondary: '#8B5CF6',
      accent: '#F472B6',
      gradient: 'from-pink-400 to-purple-400',
      glow: 'rgba(236, 72, 153, 0.3)',
      background: '#FDF2F8',
      cardBg: '#FFFFFF',
      textPrimary: '#1F2937',
      textSecondary: '#4B5563',
      textMuted: '#9CA3AF',
    },
    terminology: {
      appName: 'Pet Parent HQ',
      userRole: 'Pet Parent',
      dayLabel: 'Day with Buddy',
      dashboard: 'Pet Den',
      communication: 'Pet Talk',
      communicationDesc: 'Share your feelings',
      communicationAction: 'Send Note',
      missionUpdate: 'Pup Check-in',
      missionUpdateDesc: 'How are you and your pet?',
      missionStats: 'Pet Progress',
      missionStatsDesc: 'See your journey',
      mayday: 'SOS',
      maydayDesc: 'Emergency Info',
      riskMeter: 'Stress-O-Meter',
      riskMeterDesc: 'How stressed are you feeling?',
      freezeLog: 'Hiding Spots',
      freezeLogDesc: 'What made you want to hide?',
      dailyCheckin: 'Evening Cuddle',
      dailyCheckinDesc: 'How was your day together?',
      streak: 'Care Streak',
      courageLogs: 'Brave Moments',
      signalsSent: 'Notes Shared',
      encouragement: 'Every day with your pet is a gift. You\'re doing great, Pet Parent.',
      statsEncouragement: 'Small steps lead to big adventures. Keep going.',
    },
    quickMessages: [
      "Feeling okay",
      "Need quiet time",
      "Feeling anxious",
      "Want to cuddle",
      "Having a cozy day",
    ],
  },
};

const ExperienceThemeContext = createContext(null);

export const useExperienceTheme = () => {
  const context = useContext(ExperienceThemeContext);
  if (!context) {
    throw new Error('useExperienceTheme must be used within an ExperienceThemeProvider');
  }
  return context;
};

export const ExperienceThemeProvider = ({ children }) => {
  const [experienceTheme, setExperienceTheme] = useState('');
  const [loading, setLoading] = useState(true);

  const currentTheme = themeConfigs[experienceTheme] || themeConfigs.space;
  const needsOnboarding = !experienceTheme;

  useEffect(() => {
    // Load from localStorage first
    const saved = localStorage.getItem('experienceTheme');
    if (saved && themeConfigs[saved]) {
      setExperienceTheme(saved);
      applyThemeColors(saved);
    }
    setLoading(false);
  }, []);

  const applyThemeColors = (themeId) => {
    const theme = themeConfigs[themeId];
    if (!theme) return;
    
    const root = document.documentElement;
    root.style.setProperty('--theme-primary', theme.colors.primary);
    root.style.setProperty('--theme-secondary', theme.colors.secondary);
    root.style.setProperty('--theme-accent', theme.colors.accent);
    root.style.setProperty('--theme-glow', theme.colors.glow);
    root.style.setProperty('--theme-background', theme.colors.background);
    root.style.setProperty('--theme-card-bg', theme.colors.cardBg);
    root.style.setProperty('--theme-text-primary', theme.colors.textPrimary);
    root.style.setProperty('--theme-text-secondary', theme.colors.textSecondary);
    root.style.setProperty('--theme-text-muted', theme.colors.textMuted);
    
    // Apply experience-specific light/dark mode
    if (theme.useLightMode) {
      root.classList.add('experience-light');
      root.classList.remove('experience-dark');
    } else {
      root.classList.add('experience-dark');
      root.classList.remove('experience-light');
    }
  };

  const selectTheme = async (themeId) => {
    if (!themeConfigs[themeId]) return;
    
    setExperienceTheme(themeId);
    localStorage.setItem('experienceTheme', themeId);
    applyThemeColors(themeId);

    // Save to backend
    try {
      await fetch(`${API}/settings/experience-theme`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ experience_theme: themeId }),
      });
    } catch (error) {
      console.error('Failed to save experience theme:', error);
    }
  };

  const fetchThemeFromServer = async () => {
    try {
      const response = await fetch(`${API}/settings/experience-theme`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        if (data.experience_theme && themeConfigs[data.experience_theme]) {
          setExperienceTheme(data.experience_theme);
          localStorage.setItem('experienceTheme', data.experience_theme);
          applyThemeColors(data.experience_theme);
        }
      }
    } catch (error) {
      console.error('Failed to fetch experience theme:', error);
    }
  };

  return (
    <ExperienceThemeContext.Provider value={{
      experienceTheme,
      currentTheme,
      needsOnboarding,
      loading,
      selectTheme,
      fetchThemeFromServer,
      themeConfigs,
    }}>
      {children}
    </ExperienceThemeContext.Provider>
  );
};

export { themeConfigs };
export default ExperienceThemeContext;
